/**
 * main.ts — Entry do hub.
 *
 * 1. Inicia o canvas de particulas (fundo)
 * 2. Popula o grid de jogos a partir de jogos.ts
 * 3. Em background, consulta /quem-sou e atualiza o chip de login
 * 4. Smooth scroll do CTA "Comecar a jogar" -> secao de jogos
 */

import { JOGOS, CAPAS, type Jogo } from './jogos';
import { HubApi } from './HubApi';
import { iniciarParticulas } from './particulas';

const api = new HubApi();

iniciarFundo();
renderGrid(JOGOS);
atualizarGridDoCatalogo();
atualizarChipLogin();
ligarCta();

// ---------------------------------------------------------------------------
// Fundo animado
// ---------------------------------------------------------------------------

function iniciarFundo(): void {
  const canvas = document.getElementById('particulas');
  if (canvas instanceof HTMLCanvasElement) {
    iniciarParticulas(canvas);
  }
}

// ---------------------------------------------------------------------------
// CTA principal — scroll suave ate a secao de jogos
// ---------------------------------------------------------------------------

function ligarCta(): void {
  const cta = document.getElementById('ctaJogar');
  if (!cta) return;
  cta.addEventListener('click', (ev) => {
    ev.preventDefault();
    const alvo = document.querySelector('.secao-jogos');
    if (alvo instanceof HTMLElement) {
      alvo.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });
}

// ---------------------------------------------------------------------------
// Grid de jogos
// ---------------------------------------------------------------------------

function renderGrid(jogos: Jogo[]): void {
  const grid = document.getElementById('gridJogos');
  if (!grid) return;

  grid.innerHTML = '';
  for (const j of jogos) {
    grid.appendChild(renderCard(j));
  }
}

/**
 * Catalogo dinamico (jogo_catalogo do monolito, migr 300 — P-166 F1).
 * A lista hardcoded de jogos.ts vira FALLBACK: renderiza na hora (zero
 * flicker de rede) e e substituida quando a API responde.
 */
async function atualizarGridDoCatalogo(): Promise<void> {
  try {
    const doBanco = await api.catalogo();
    const jogos: Jogo[] = doBanco.map((j) => ({
      id: j.slug,
      titulo: j.titulo,
      descricao: j.descricao,
      url: j.url,
      status: j.status,
      icone: j.icone,
      gradiente: j.gradiente,
      // A arte e' do hub, nao do banco: o catalogo manda texto/ordem/status,
      // a capa vem de public/thumbs pelo slug (jogo novo sem capa cai no emoji).
      capa: CAPAS[j.slug],
      meta: j.meta && j.meta.length > 0 ? j.meta : undefined,
    }));
    renderGrid(jogos);
  } catch {
    // API fora ou vazia: a lista hardcoded ja esta na tela
  }
}

function renderCard(j: Jogo): HTMLElement {
  const card = document.createElement('a');
  card.className = `card-jogo${j.status === 'em-breve' ? ' em-breve' : ''}`;
  card.href = j.url;
  card.dataset.jogo = j.id;
  card.setAttribute('aria-label', `Jogar ${j.titulo}`);

  const thumb = document.createElement('div');
  thumb.className = 'card-thumb';
  thumb.style.setProperty('--card-gradient', j.gradiente);
  // Com capa: a foto da tela real do jogo. Sem capa: o emoji de sempre.
  const arte = j.capa
    ? `<img class="card-thumb-img" src="${escapeHtml(j.capa)}" alt="Tela do ${escapeHtml(j.titulo)}"
            width="800" height="450" loading="lazy" decoding="async">`
    : `<span class="card-thumb-icone" aria-hidden="true">${j.icone}</span>`;
  thumb.innerHTML = `
    ${arte}
    <span class="card-tag" data-status="${j.status}">${rotuloStatus(j.status)}</span>
  `;

  const conteudo = document.createElement('div');
  conteudo.className = 'card-conteudo';
  conteudo.innerHTML = `
    <h3 class="card-titulo">${escapeHtml(j.titulo)}</h3>
    <p class="card-descricao">${escapeHtml(j.descricao)}</p>
    ${j.meta ? renderMeta(j.meta) : ''}
    ${j.status !== 'em-breve' ? renderCtaCard() : ''}
  `;

  card.appendChild(thumb);
  card.appendChild(conteudo);
  return card;
}

function renderMeta(meta: NonNullable<Jogo['meta']>): string {
  return `
    <div class="card-meta">
      ${meta.map((m) => `<span>${m.icone} ${escapeHtml(m.texto)}</span>`).join('')}
    </div>
  `;
}

function renderCtaCard(): string {
  return `
    <div class="card-cta">
      <span>Jogar agora</span>
      <svg class="card-cta-seta" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M5 12h14M13 6l6 6-6 6" />
      </svg>
    </div>
  `;
}

function rotuloStatus(s: Jogo['status']): string {
  switch (s) {
    case 'estavel': return 'Estavel';
    case 'beta':    return 'Beta';
    case 'em-breve':return 'Em breve';
  }
}

// ---------------------------------------------------------------------------
// Chip de login (canto superior direito)
// ---------------------------------------------------------------------------

async function atualizarChipLogin(): Promise<void> {
  const chip = document.getElementById('chipLogin');
  if (!chip) return;

  const r = await api.quemSou();

  if (r.logado && r.usuario) {
    chip.dataset.estado = 'ok';
    chip.innerHTML = `
      <span class="chip-ponto"></span>
      <span class="chip-texto">${escapeHtml(r.usuario.nome)}</span>
    `;
    if (chip instanceof HTMLAnchorElement) chip.removeAttribute('href');
  } else {
    chip.dataset.estado = 'deslogado';
    chip.innerHTML = `
      <span class="chip-ponto"></span>
      <span class="chip-texto">Entrar</span>
    `;
    const link = document.createElement('a');
    link.className = chip.className;
    link.dataset.estado = chip.dataset.estado;
    link.id = chip.id;
    link.href = api.loginUrl;
    link.innerHTML = chip.innerHTML;
    chip.replaceWith(link);
  }
}

// ---------------------------------------------------------------------------

function escapeHtml(s: string): string {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  })[c] || c);
}
